<template>
  <div class="settings">
    <div class="settings-panel">
      <aside class="settings-sidebar">
        <div class="settings-sidebar-header">
          <BaseIcon name="settings" type="global" />
          <span>Settings</span>
        </div>
        <nav>
          <div
            v-for="group in navGroups"
            :key="group.label"
            class="settings-nav-group"
          >
            <div class="settings-nav-group-title">{{ group.label }}</div>
            <router-link
              v-for="item in group.items"
              :key="item.slug"
              :to="'/settings/' + item.slug"
              class="settings-nav-item"
              :class="{ active: currentPage === item.slug }"
            >
              <BaseIcon :name="item.icon" :type="item.iconType || 'global'" />
              <span>{{ item.label }}</span>
            </router-link>
          </div>
        </nav>
      </aside>
      <main class="settings-content-area">
        <keep-alive :include="cachedComponents">
          <component :is="currentTabComponent" />
        </keep-alive>
      </main>
    </div>
  </div>
</template>

<script>
import BaseIcon from "../../components/icons/BaseIcon";
import { defineAsyncComponent, markRaw } from "vue";

const pageMap = {
  account: "Account",
  billing: "Billing",
  general: "General",
  "line-options": "LineOptions",
  "table-views": "TableViewSettings",
  collection: "CollectionSettings",
  problems: "Problems",
  crm: "Crm",
  products: "Products",
  assets: "Assets",
  "price-comparison": "PriceComparison",
  "sales-settings": "Sales",
  "income-statement": "IncomeStatementSettings",
  "ai-workflows": "AiWorkflows",
  users: "Users",
  companies: "Companies",
  warehouses: "Warehouses",
  events: "Events",
  "sale-channels": "webshops",
  domains: "Domains",
  countries: "Countries",
  couriers: "Couriers",
  returns: "Returns",
  integrations: "integrations",
};

const navGroups = [
  {
    label: "Account",
    items: [
      {
        slug: "account",
        label: "My Account",
        icon: "user",
        iconType: "settings",
      },
      {
        slug: "billing",
        label: "Billing & Plans",
        icon: "billing",
        iconType: "settings",
      },
    ],
  },
  {
    label: "Tools",
    items: [
      { slug: "general", label: "General", icon: "settings" },
      {
        slug: "line-options",
        label: "Line Options",
        icon: "tools",
        iconType: "settings",
      },
      { slug: "table-views", label: "Table Views", icon: "menu" },
      { slug: "collection", label: "Collection", icon: "box" },
      { slug: "problems", label: "Problems", icon: "warning" },
      { slug: "crm", label: "CRM", icon: "customers" },
      { slug: "products", label: "Products", icon: "box2" },
      { slug: "assets", label: "Assets", icon: "assets" },
      { slug: "price-comparison", label: "Price Comparison", icon: "chart-3" },
      { slug: "sales-settings", label: "Sales", icon: "cart" },
      { slug: "income-statement", label: "Income Statement", icon: "document" },
    ],
  },
  {
    label: "AI",
    items: [
      { slug: "ai-workflows", label: "AI Workflows", icon: "diagram" }
    ],
  },
  {
    label: "Organization",
    items: [
      {
        slug: "companies",
        label: "Companies",
        icon: "briefcase",
        iconType: "settings",
      },
      { slug: "warehouses", label: "Warehouses", icon: "buildings" },
      {
        slug: "users",
        label: "Team Members",
        icon: "group",
        iconType: "settings",
      },
    ],
  },
  {
    label: "Sales Channels",
    items: [
      { slug: "events", label: "Events", icon: "calendar" },
      {
        slug: "sale-channels",
        label: "Sale Channels",
        icon: "shopping",
        iconType: "settings",
      },
      { slug: "domains", label: "Domains", icon: "web" },
      { slug: "countries", label: "Countries", icon: "flag" },
      { slug: "couriers", label: "Couriers", icon: "truck-time" },
      { slug: "returns", label: "Returns", icon: "change" },
    ],
  },
  {
    label: "Integrations",
    items: [
      {
        slug: "integrations",
        label: "Integrations",
        icon: "puzzle",
        iconType: "settings",
      },
    ],
  },
];

export default {
  name: "Settings",
  components: { BaseIcon },
  data() {
    return {
      navGroups,
      currentTabComponent: markRaw(
        defineAsyncComponent(() =>
          import("../../components/settings/Start.vue")
        )
      ),
    };
  },
  computed: {
    currentPage() {
      return this.$route.params.page || "start";
    },
    cachedComponents() {
      return ["Start", ...Object.values(pageMap)];
    },
  },
  watch: {
    currentPage(page) {
      if (page === "start") {
        this.currentTabComponent = markRaw(
          defineAsyncComponent(() =>
            import("../../components/settings/Start.vue")
          )
        );
      } else {
        const componentName = pageMap[page];
        if (componentName) {
          this.currentTabComponent = markRaw(
            defineAsyncComponent(() =>
              import(`../../components/settings/${componentName}.vue`)
            )
          );
        } else {
          this.$router.push("/settings");
        }
      }
    },
  },
  created() {
    const page = this.$route.params.page;
    if (page && pageMap[page]) {
      const componentName = pageMap[page];
      this.currentTabComponent = markRaw(
        defineAsyncComponent(() =>
          import(`../../components/settings/${componentName}.vue`)
        )
      );
    } else if (page) {
      this.$router.push("/settings");
    }
  },
};
</script>
